// Flat, searchable index of every destination reachable from the primary nav.
//
// Built once at module load from `dummyMenuData` — the nav data is static, so there is
// no reason to re-walk it on every keystroke. The nav dropdown stays the single source
// of truth: add a link there and it becomes searchable here automatically.
import {dummyMenuData} from './index';
import type {NavCategory, NavLink, NavMenuGroup, NavMenuSection, NavSubmenuItem, NavTopLevelItem} from '@/types/navigation';

export interface NavSearchEntry {
    label: string; // link text, e.g. "AI Chatbot Development"
    path: string; // route, e.g. "/ai-chatbot-development-company"
    group: string; // where it lives, e.g. "Services · Conversational & Agentic AI"
    target?: string;
    /** lowercased `label + group`, precomputed so matching is a plain substring test */
    haystack: string;
}

/** Minimum characters before the static index is consulted at all. */
export const MIN_SEARCH_LENGTH = 2;

const buildIndex = (): NavSearchEntry[] => {
    const entries: NavSearchEntry[] = [];
    const seen = new Set<string>();

    const push = (label: string | undefined, path: string | undefined, group: string, target?: string) => {
        if (!label || !path || seen.has(path)) return;
        seen.add(path);
        entries.push({label, path, group, target, haystack: `${label} ${group}`.toLowerCase()});
    };

    dummyMenuData?.forEach((top: NavTopLevelItem) => {
        const topLabel = top.btnLabel ?? '';

        if (top.path) push(topLabel, top.path, topLabel);

        top.menus?.forEach((menu: NavMenuGroup) => {
            const section: NavMenuSection | undefined = menu?.menuItems;
            if (!section) return;
            const sectionLabel = section.heading || section.header || '';

            // Card-grid sections (Services, Products, Industries)
            section.categories?.forEach((category: NavCategory) => {
                const group = [topLabel, category.categoryHeading].filter(Boolean).join(' · ');
                category.links?.forEach((link: NavLink) => push(link.label, link.path, group, link.target));
            });

            // Flat sections (Resources, Company) — the heading itself is the link
            if (section.multiple && section.path) {
                push(sectionLabel, section.path, topLabel, section.target);
            }

            // Nested submenu links
            section.submenu?.forEach((child: NavSubmenuItem) => {
                const group = [topLabel, sectionLabel].filter(Boolean).join(' · ');
                push(child.heading, child.path, group, child.target);
            });
        });
    });

    return entries;
};

export const navSearchIndex: NavSearchEntry[] = buildIndex();

/** Case-insensitive substring match, label matches ranked above group matches. */
export const searchNavLinks = (query: string, limit = 8): NavSearchEntry[] => {
    const term = query.trim().toLowerCase();
    if (term.length < MIN_SEARCH_LENGTH) return [];

    const starts: NavSearchEntry[] = [];
    const contains: NavSearchEntry[] = [];

    for (const entry of navSearchIndex) {
        const label = entry.label.toLowerCase();
        if (label.startsWith(term)) starts.push(entry);
        else if (entry.haystack.includes(term)) contains.push(entry);
        if (starts.length >= limit) break;
    }

    return [...starts, ...contains].slice(0, limit);
};
